// AI Tutor streaming presentation (doc 27) -- what the presentation layer
// asks of the browser, in one place.
//
// This is the manual-injection seam its unit tests use (`jest.mock` is
// unusable in this repo, see store/modules/aiTutor.js `collaborators`): a spec
// reassigns these fields to a fake clock, a fake frame queue and a fixed
// motion preference, and restores them afterwards.
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

export const revealEnvironment = {
  now: () => Date.now(),

  requestFrame: callback => {
    if (typeof window.requestAnimationFrame === 'function') {
      return window.requestAnimationFrame(callback)
    }
    return window.setTimeout(callback, 16)
  },

  cancelFrame: id => {
    if (typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(id)
    } else {
      window.clearTimeout(id)
    }
  },

  setInterval: (callback, ms) => window.setInterval(callback, ms),
  clearInterval: id => window.clearInterval(id),

  /**
   * @returns {boolean|null} whether the student asked for reduced motion;
   *   `null` when the preference cannot be read. Unknown is treated as "do
   *   not animate" (doc 27 §28.5): the text is then simply shown as it
   *   arrives, which is the accepted chat's own behaviour.
   */
  prefersReducedMotion: () => {
    if (typeof window === 'undefined') return null
    if (typeof window.matchMedia !== 'function') return null
    const query = window.matchMedia(REDUCED_MOTION)
    return query ? !!query.matches : null
  },

  /**
   * @param {() => void} listener called when the preference changes
   * @returns {() => void} stops listening
   */
  watchReducedMotion: listener => {
    if (typeof window === 'undefined') return () => {}
    if (typeof window.matchMedia !== 'function') return () => {}
    const query = window.matchMedia(REDUCED_MOTION)
    if (!query) return () => {}
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', listener)
      return () => query.removeEventListener('change', listener)
    }
    if (typeof query.addListener === 'function') {
      query.addListener(listener)
      return () => query.removeListener(listener)
    }
    return () => {}
  },

  isHidden: () =>
    typeof document !== 'undefined' && document.visibilityState === 'hidden',
}

export default revealEnvironment
